import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import moment from 'moment';
import BZAPIService from '../../services/BZAPIService';
import ExportWizard from './ExportWizard';
import ExportHistory from './ExportHistory';

const WIZARD_TAB = 'new';
const HISTORY_TAB = 'history';

// Stable ids so the tab buttons and their panels can point at each other for
// screen readers (role="tab" is meaningless without aria-controls/labelledby).
const WIZARD_TAB_ID = 'bz-export-tab-new';
const HISTORY_TAB_ID = 'bz-export-tab-history';
const WIZARD_PANEL_ID = 'bz-export-panel-new';
const HISTORY_PANEL_ID = 'bz-export-panel-history';

// Version ids are shown verbatim in the recovery notices; a project with many
// of them would otherwise turn the notice into a wall of text.
const NAMED_VERSION_LIMIT = 6;

const formatVersionList = ids => {
  if (ids.length <= NAMED_VERSION_LIMIT) return ids.join(', ');
  return `${ids.slice(0, NAMED_VERSION_LIMIT).join(', ')} and ${ids.length - NAMED_VERSION_LIMIT} more`;
};

/**
 * One integer off the counts response, or null.
 *
 * Returns null — never 0 — for anything that is not a finite number. An
 * absent count is "unknown", and ExportWizard fails closed on unknown rather
 * than pricing it at zero pages. `null`/`undefined` are rejected explicitly
 * because `Number(null)` is 0, which is exactly the fail-open substitution
 * this whole path exists to avoid.
 */
const countOf = raw => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(n, 0) : null;
};

/** Inline banner above the wizard, optionally carrying a recovery action. */
const Notice = ({ tone, children, actionLabel, onAction, actionDisabled }) => (
  <div
    role={tone === 'bad' ? 'alert' : 'status'}
    className="bz-card--flat bz-row"
    style={{
      alignItems: 'center',
      gap: 'var(--sp-3)',
      marginBottom: 'var(--sp-4)',
      padding: 'var(--sp-3) var(--sp-4)',
      borderColor: `var(--${tone})`,
      background: `var(--${tone}-soft)`,
      color: `var(--${tone})`,
      fontSize: 'var(--fs-sm)'
    }}
  >
    <span className="bz-grow">{children}</span>
    {actionLabel && (
      <button
        type="button"
        className="bz-btn bz-btn--sm"
        onClick={onAction}
        disabled={actionDisabled}
        style={{ flexShrink: 0 }}
      >
        {actionLabel}
      </button>
    )}
  </div>
);

Notice.propTypes = {
  tone: PropTypes.oneOf(['bad', 'warn']).isRequired,
  children: PropTypes.node,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  actionDisabled: PropTypes.bool
};

/**
 * The version rows ExportWizard prices and submits.
 *
 * Two data sources, JOINED — not substituted:
 *
 *   identity  `PlanProjectVersion.versionId` (from `selectedProject.versions`).
 *             This is the only key start_export_job.py's `_select_project_versions`
 *             will accept besides the row's uuid PK; anything else is a 400
 *             ("version 'X' does not belong to this project"). It also bounds
 *             the list correctly — a version with no project row cannot be
 *             exported at all, so it is not offered.
 *
 *   display   the ODD software-suggestion row (`releaseDate`,
 *   + counts   `releaseLifeCycle`, `bugCount`, `cveCount`, `riskScoreAverage`).
 *
 * The join key is `PlanProjectVersion.versionId === suggestion.suggestionId`.
 * That is how the row is written in the first place — PlanPage.js creates
 * project versions with `versionId: selectedVersion.suggestionId` — and how
 * every existing lookup reads it back (`selectedProject.versions.find(v =>
 * v.versionId === selectedVersion.suggestionId)`, and the accepted-version
 * lookup `upgradeVersions.find(uv => uv.suggestionId === pv.versionId)`).
 * A stored row confirms it: service-tools-api/plan/events/get_version_issues_event.json
 * has `"versionId": "16.1(3g)"` next to `"suggestionId": "16.1(3g)"`.
 *
 * `bsaGetBstSoftwareSuggestions` already returns `bugCount`/`cveCount`/
 * `riskScoreAverage` per suggestion, so counts are read straight off the same
 * suggestion row used for `releaseDate`/`releaseLifeCycle` — no second
 * request. This used to be a separate `bsaGetVersionItemCounts` call (and
 * before that, `2N` requests straight to the two ODD proxies returning full
 * ~3 MB bug/CVE bodies just to expose a `count` integer); both were slow
 * enough to time out on large products and left the affected versions
 * unselectable.
 *
 * A version whose counts cannot be resolved keeps `bugCount`/`cveCount`
 * ABSENT (not 0) — that is precisely the shape ExportWizard's `itemsFor`
 * reads as "unknown" and refuses to include. That only happens now for a
 * version with no matching suggestion row at all (or one missing the ODD
 * labels), which is surfaced as `unmatchedVersions`. A matched suggestion
 * with `bugCount: 0` is a legitimate zero, not unknown.
 */
const ExportModal = ({ isOpen, toggle, selectedProject, currentUserEmail, canCreateExport }) => {
  const [activeTab, setActiveTab] = useState(canCreateExport ? WIZARD_TAB : HISTORY_TAB);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [versionsError, setVersionsError] = useState(null);
  // Versions the vendor's release catalogue has no usable row for. NOT
  // recoverable by retrying, but must still be named: the server would accept
  // them, and without this the user just sees a permanently disabled row.
  const [unmatchedVersions, setUnmatchedVersions] = useState([]);
  // The vendor's priority scale + default exclusion set, read off the software
  // suggestions and handed to the wizard's priority-exclusion control. ODD adds
  // `availablePriorities`/`excludedPriorities` to each suggestion; they're
  // identical across a vendor's versions, so we take them off the first
  // suggestion that carries them. Absent (today's reality on every tenant until
  // ODD ships them) => empty, and the wizard hides the control entirely.
  const [priorityMeta, setPriorityMeta] = useState({ available: [], excluded: [] });
  // Bumped after a successful start so the history tab remounts and re-lists,
  // showing the new job straight away.
  const [historyKey, setHistoryKey] = useState(0);
  // Bumped for the same reason in the other direction — see onStarted.
  const [wizardKey, setWizardKey] = useState(0);
  // Discards results from a superseded load (project changed, modal closed).
  const loadToken = useRef(0);

  const projectId = selectedProject?.id;
  const vendorId = selectedProject?.vendorId;
  const productId = selectedProject?.productId;
  const projectVersions = selectedProject?.versions;

  const buildVersions = useCallback(async () => {
    loadToken.current += 1;
    const token = loadToken.current;
    const isStale = () => token !== loadToken.current;

    // Identity first, from the project's own version rows. Deduplicated because
    // versionId is what both the wizard and the server key on.
    const seen = new Set();
    const base = [];
    (projectVersions || []).forEach(pv => {
      const versionId = pv?.versionId;
      if (!versionId || seen.has(versionId)) return;
      seen.add(versionId);
      base.push({ versionId, state: pv.state });
    });

    setVersions(base);
    setVersionsError(null);
    setUnmatchedVersions([]);
    setPriorityMeta({ available: [], excluded: [] });
    if (!base.length) return;

    setLoadingVersions(true);
    try {
      let suggestions = [];
      try {
        // No `current_version`: that filters to upgrades newer than the
        // project's base version, and a project version can legitimately be
        // the base itself. The unfiltered list is a superset.
        const data = await BZAPIService.bsaGetBstSoftwareSuggestions(vendorId, productId);
        suggestions = data?.results || [];
      } catch (error) {
        console.error('Failed to load version details:', error);
        if (isStale()) return;
        setVersionsError('Version details could not be loaded, so report size cannot be estimated.');
        return;
      }
      if (isStale()) return;

      const byId = new Map();
      suggestions.forEach(s => {
        if (s?.suggestionId !== undefined && s?.suggestionId !== null) byId.set(String(s.suggestionId), s);
      });

      // A project version with no catalogue row — or a row missing the ODD
      // labels the counts are keyed by — can never be priced, so it can never
      // be selected. That is the only "unknown" bucket now: a matched
      // suggestion's `bugCount`/`cveCount` of 0 is a real, selectable count,
      // not a stand-in for "not loaded yet".
      const unmatched = [];
      const merged = base.map(v => {
        const s = byId.get(String(v.versionId));
        if (!s) {
          unmatched.push(v.versionId);
          return v;
        }
        if (!s.labelBug || !s.labelCve || !s.softwareType) unmatched.push(v.versionId);
        return {
          ...v,
          releaseDate: s.releaseDate ? moment.utc(s.releaseDate).format('MMM D, YYYY') : '',
          // Sortable companion to the display string above: a millisecond
          // epoch (not the formatted string), so ExportWizard's version-table
          // sort can compare dates chronologically instead of lexically.
          // `null` when there's no source date, matching the "unknown sorts
          // to the bottom" handling used for the other columns.
          releaseDateRaw: s.releaseDate ? moment.utc(s.releaseDate).valueOf() : null,
          releaseLifeCycle: s.releaseLifeCycle || '',
          // Prefer the priority-scrubbed count (irrelevant priorities excluded)
          // when the suggestion carries one, else fall back to the total, so the
          // wizard's estimate reflects what will actually render once the filter
          // is live. Explicit `!== undefined && !== null` because a real 0 scrub
          // count is valid, not a fallback trigger. Until ODD populates these
          // fields (today's reality on every tenant) both resolve to the total,
          // leaving countOf's unknown-vs-zero behaviour identical to before.
          bugCount: countOf(s.bugCountScrub !== undefined && s.bugCountScrub !== null ? s.bugCountScrub : s.bugCount),
          cveCount: countOf(s.cveCountScrub !== undefined && s.cveCountScrub !== null ? s.cveCountScrub : s.cveCount),
          riskScoreAverage: s.riskScoreAverage ?? null
        };
      });
      setVersions(merged);
      setUnmatchedVersions(unmatched);

      // Priority scale + vendor default for the wizard's exclusion control.
      // Identical across a vendor's versions, so read them off the first
      // suggestion that actually carries a non-empty scale. Missing => stays
      // empty and the wizard hides the control.
      const withScale = suggestions.find(
        s => Array.isArray(s?.availablePriorities) && s.availablePriorities.length > 0
      );
      setPriorityMeta({
        available: withScale ? withScale.availablePriorities.filter(p => typeof p === 'string' && p) : [],
        excluded:
          withScale && Array.isArray(withScale.excludedPriorities)
            ? withScale.excludedPriorities.filter(p => typeof p === 'string' && p)
            : []
      });
    } finally {
      if (!isStale()) setLoadingVersions(false);
    }
  }, [projectVersions, vendorId, productId]);

  useEffect(() => {
    if (isOpen && canCreateExport && projectId) buildVersions();
  }, [isOpen, canCreateExport, projectId, buildVersions]);

  const onStarted = () => {
    // reactstrap hides an inactive TabPane rather than unmounting it, so
    // without a remount the wizard is still sitting on step 3 with the same
    // selection and an enabled "Start export" when the user tabs back — one
    // click from an identical multi-minute render. Remounting resets it to a
    // fresh step 0, which is what the pre-wizard modal did after a create.
    setWizardKey(k => k + 1);
    setHistoryKey(k => k + 1);
    setActiveTab(HISTORY_TAB);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Export BugZero Plan Project Report</ModalHeader>
      <ModalBody>
        <div className="bz-app" style={{ background: 'transparent' }}>
          {canCreateExport && (
            <Nav tabs className="mb-4" role="tablist">
              <NavItem role="presentation">
                <NavLink
                  href="#"
                  id={WIZARD_TAB_ID}
                  role="tab"
                  aria-selected={activeTab === WIZARD_TAB}
                  aria-controls={WIZARD_PANEL_ID}
                  active={activeTab === WIZARD_TAB}
                  onClick={e => {
                    e.preventDefault();
                    setActiveTab(WIZARD_TAB);
                  }}
                >
                  New export
                </NavLink>
              </NavItem>
              <NavItem role="presentation">
                <NavLink
                  href="#"
                  id={HISTORY_TAB_ID}
                  role="tab"
                  aria-selected={activeTab === HISTORY_TAB}
                  aria-controls={HISTORY_PANEL_ID}
                  active={activeTab === HISTORY_TAB}
                  onClick={e => {
                    e.preventDefault();
                    setActiveTab(HISTORY_TAB);
                  }}
                >
                  History
                </NavLink>
              </NavItem>
            </Nav>
          )}

          <TabContent activeTab={activeTab}>
            {canCreateExport && (
              <TabPane
                tabId={WIZARD_TAB}
                id={WIZARD_PANEL_ID}
                role="tabpanel"
                aria-labelledby={WIZARD_TAB_ID}
                tabIndex={0}
              >
                {versionsError && (
                  <Notice
                    tone="bad"
                    actionLabel={loadingVersions ? 'Retrying…' : 'Try again'}
                    onAction={buildVersions}
                    actionDisabled={loadingVersions}
                  >
                    {versionsError}
                  </Notice>
                )}
                {!versionsError && unmatchedVersions.length > 0 && (
                  <Notice tone="warn">
                    {formatVersionList(unmatchedVersions)}{' '}
                    {unmatchedVersions.length === 1 ? 'has no release details' : 'have no release details'} in this
                    product&apos;s catalogue, so{' '}
                    {unmatchedVersions.length === 1 ? 'its size cannot be' : 'their sizes cannot be'} estimated and{' '}
                    {unmatchedVersions.length === 1 ? 'it cannot be' : 'they cannot be'} included. Retrying will not
                    change this.
                  </Notice>
                )}
                {loadingVersions && (
                  <div className="bz-muted" style={{ fontSize: 'var(--fs-sm)', marginBottom: 'var(--sp-3)' }}>
                    Loading version details…
                  </div>
                )}
                {selectedProject && (
                  <ExportWizard
                    key={wizardKey}
                    project={selectedProject}
                    versions={versions}
                    currentUserEmail={currentUserEmail}
                    onStarted={onStarted}
                    availablePriorities={priorityMeta.available}
                    defaultExcludedPriorities={priorityMeta.excluded}
                  />
                )}
              </TabPane>
            )}
            <TabPane
              tabId={HISTORY_TAB}
              id={HISTORY_PANEL_ID}
              {...(canCreateExport ? { role: 'tabpanel', 'aria-labelledby': HISTORY_TAB_ID, tabIndex: 0 } : {})}
            >
              {projectId && <ExportHistory key={historyKey} projectId={projectId} />}
            </TabPane>
          </TabContent>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button className="btn btn-falcon-default" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ExportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  selectedProject: PropTypes.object,
  currentUserEmail: PropTypes.string,
  canCreateExport: PropTypes.bool
};

export default ExportModal;
